import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { dialogs, i18next, view } from '@yola/ws-sdk';
import { context as viewportContext } from 'yola-editor/src/js/utils/viewport-provider';
import ButtonPresetSettingsDialog from '../components/button-preset-settings-dialog';
import helpers from '../helpers';
import dialogTypes from '../../dialogs/constants/dialog-types';
import {
  SUBMIT_BUTTON_TRIGGER_ID,
  DIALOG_BACKGROUND_TRIGGER_ID,
} from '../../dialogs/constants/common';
import dialogCallSourceIds from '../constants/dialog-call-source-ids';
import buttonPresetSectionIds from '../constants/button-preset-section-ids';
import triggerIds from '../constants/trigger-ids';

const { readOnlyModeDecorator, getTargetSurfaceForButtonPreset } = helpers;

const getCaptions = () => ({
  breadcrumbs: [i18next.t('Design'), i18next.t('Buttons')],
  title: i18next.t('Edit preset'),
  description: i18next.t('The changes will apply to all buttons across your website'),
  settingTitle: i18next.t('Color'),
  settingLabel: i18next.t('Manage colors'),
  submitButton: i18next.t('Save'),
  cancelButton: i18next.t('Cancel'),
});

const getSettingsSections = () => [
  {
    id: buttonPresetSectionIds.PRIMARY_BUTTON,
    title: i18next.t('Primary button'),
  },
  {
    id: buttonPresetSectionIds.SECONDARY_BUTTON,
    title: i18next.t('Secondary button'),
  },
];

const BUTTON_TYPE_TO_TRIGGER_ID = {
  [buttonPresetSectionIds.PRIMARY_BUTTON]: triggerIds.PRIMARY_BUTTON_COLOR,
  [buttonPresetSectionIds.SECONDARY_BUTTON]: triggerIds.SECONDARY_BUTTON_COLOR,
};

const ButtonPresetSettingsContainer = ({
  presetId,
  activeButtonPresetId,
  onDialogMainAction,
  onDialogCancel,
  getSharedData,
  resolveSharedData,
}) => {
  const { offsetX, offsetY } = getSharedData();

  const [dialogPosition, updateDialogPosition] = useState({
    offsetX,
    offsetY,
  });

  const scrollPosition = useSelector(view.selectors.getScrollPosition);
  const { height: viewportHeight } = useContext(viewportContext);

  const captions = getCaptions();
  const settingsSections = getSettingsSections();

  const dispatch = useDispatch();

  const updateSharedData = (updatedData = {}) => {
    resolveSharedData({
      offsetX: dialogPosition.offsetX,
      offsetY: dialogPosition.offsetY,
      ...updatedData,
    });
  };

  const openAdvancedColorsSettings = (buttonType, searchQuery) => {
    const colorSchemeId = getTargetSurfaceForButtonPreset(
      buttonType,
      scrollPosition,
      viewportHeight
    );

    updateSharedData({
      editingButtonPresetId: presetId,
      searchQuery,
    });

    dispatch(
      dialogs.actions.show(dialogTypes.ADVANCED_COLORS_SETTINGS_DIALOG, {
        colorSchemeId,
        triggerId: BUTTON_TYPE_TO_TRIGGER_ID[buttonType],
      })
    );
  };

  const handleDragEnd = (_, { x, y }) => {
    updateDialogPosition({ offsetX: x, offsetY: y });
  };

  const handleCancel = () => {
    onDialogCancel({
      buttonPreset: presetId,
    });
    updateSharedData();
    dispatch(
      dialogs.actions.show(dialogTypes.DESIGN_SETTINGS_BUTTONS_DIALOG, {
        sourceId: dialogCallSourceIds.BACK_FROM_BUTTON_PRESET_SETTINGS,
        activePresetId: activeButtonPresetId,
      })
    );
  };

  const handleSubmit = ({ triggerId = SUBMIT_BUTTON_TRIGGER_ID }) => {
    onDialogMainAction({
      triggerId,
      buttonPreset: presetId,
    });
    updateSharedData();
    dispatch(
      dialogs.actions.show(dialogTypes.DESIGN_SETTINGS_BUTTONS_DIALOG, {
        sourceId: dialogCallSourceIds.BACK_FROM_BUTTON_PRESET_SETTINGS,
        activePresetId: presetId,
      })
    );
  };

  const handleOverlayClick = () => handleSubmit({ triggerId: DIALOG_BACKGROUND_TRIGGER_ID });

  return (
    <ButtonPresetSettingsDialog
      offsetX={offsetX}
      offsetY={offsetY}
      captions={captions}
      settingsSections={settingsSections}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      onOverlayClick={handleOverlayClick}
      onManageColors={readOnlyModeDecorator(openAdvancedColorsSettings)}
      onDragEnd={handleDragEnd}
    />
  );
};

ButtonPresetSettingsContainer.propTypes = {
  presetId: PropTypes.string.isRequired,
  activeButtonPresetId: PropTypes.string.isRequired,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
  getSharedData: PropTypes.func.isRequired,
  resolveSharedData: PropTypes.func.isRequired,
};

export default ButtonPresetSettingsContainer;
